import * as swcHelpers from '@swc/helpers';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useReplicateCart } from '../modules/QrCode/hooks/use-replicate-cart.js';
import { usePaymentsUIContext } from '../../../context/PaymentsUI/context.js';
import { PaymentFlow } from '../../../models/payment/types.js';

const useQrPaymentState = ({ paymentState , orderId: orderId1 , error , paymentResponse , orderToken , onQrCodeRefresh , refetch , paymentFlow , cart ,  })=>{
    const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
    const [isQrRefreshLoading, setIsQrRefreshLoading] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [qrCodeExpireIn, setQrCodeExpireIn] = useState(0);
    const [externalUrl, setExternalUrl] = useState('');
    const [replicateCart] = useReplicateCart(refetch);
    const { setPaymentQRCodeState , isQrCodeModalOpen , setIsQrCodeModalOpen  } = usePaymentsUIContext();
    /*
   *  When user cancels payment or payment fails
   *  actions: 1. Replicate
   *           2. Call refetch
   *           4. Close QR modal
   */ const cancelPayment = useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderId) {
            const { cartId  } = yield replicateCart(orderId);
            refetch(cartId);
            setIsQrCodeModalOpen(false);
        });
        return function(orderId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        refetch,
        replicateCart,
        setIsQrCodeModalOpen
    ]);
    /*
   *  Listen for QR code payment status
   *  if paymentState is updated set paymentQRCodeState in PaymentsUI Context
   *  if PaymentState is 'Failed' or any error occurred, replicate and restore cart
   */ useEffect(()=>{
        if (paymentState) {
            setPaymentQRCodeState(paymentState);
        }
        if (paymentState === 'Failed' || error) {
            cancelPayment(orderId1);
        }
    }, [
        paymentState,
        error,
        cancelPayment,
        orderId1,
        orderToken,
        setPaymentQRCodeState
    ]);
    /*
   *  Reset QR code timeout
   *  Refresh QR code image
   */ useEffect(()=>{
        if (qrCodeExpireIn) {
            const timeoutId = setTimeout(()=>{
                setIsQrCodeExpired(true);
                setQrCodeExpireIn(0);
            }, qrCodeExpireIn);
            return ()=>clearTimeout(timeoutId)
            ;
        }
    }, [
        qrCodeExpireIn
    ]);
    /*
   *  Set/Update properties when QR code is updated
   */ useEffect(()=>{
        setQrCodeExpireIn(Number(paymentResponse.qrCodeExpiresIn) * 1000);
        setQrCode(String(paymentResponse.qrCodeUrl));
        setExternalUrl(String(paymentResponse.redirectUrl));
        setIsQrCodeExpired(false);
        setIsQrRefreshLoading(false);
    }, [
        paymentResponse.qrCodeExpiresIn,
        paymentResponse.qrCodeUrl,
        paymentResponse.redirectUrl
    ]);
    useEffect(()=>{
        if (paymentFlow === PaymentFlow.QR_CODE) {
            setIsQrCodeModalOpen(true);
        }
    }, [
        paymentFlow,
        setIsQrCodeModalOpen
    ]);
    /*
   *  On refreshQr replicate cart
   *  Get latest QR code
   */ const handleRefreshQrCode = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (cart && orderId1) {
            setQrCodeExpireIn(0);
            setIsQrCodeExpired(true);
            yield replicateCart(orderId1);
            onQrCodeRefresh();
        }
        setIsQrRefreshLoading(true);
    }), [
        cart,
        onQrCodeRefresh,
        orderId1,
        replicateCart
    ]);
    return useMemo(()=>({
            externalUrl,
            isOpen: isQrCodeModalOpen,
            isQrCodeExpired,
            isQrRefreshLoading,
            onCancel: ()=>cancelPayment(orderId1)
            ,
            qrCodeUrl: qrCode,
            refreshQrCode: ()=>handleRefreshQrCode()
        })
    , [
        cancelPayment,
        externalUrl,
        handleRefreshQrCode,
        isQrCodeExpired,
        isQrCodeModalOpen,
        isQrRefreshLoading,
        orderId1,
        qrCode, 
    ]);
};

export { useQrPaymentState };
//# sourceMappingURL=use-qr-payment-state.js.map
